import React, { useEffect, useState } from 'react';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { Avatar } from '../components/UI';
import { ChevronRight, Plus, LogOut, X, Edit2, Trash2, Check, Bell, BellOff } from 'lucide-react';
import { Template, Operator } from '../types';
import { api, TelegramOperationalStatus } from '../api';

// ── Modal wrapper ──────────────────────────────────────────────────────
const Modal = ({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) => (
  <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-t-[24px] md:rounded-[20px] w-full md:max-w-md shadow-2xl z-10 max-h-[90vh] overflow-y-auto mobile-sheet-safe">
      <div className="flex items-center justify-between px-6 py-4 border-b border-black/5 sticky top-0 bg-white/95 backdrop-blur-xl z-10">
        <h3 className="text-[18px] font-semibold text-black">{title}</h3>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-black/5 rounded-full hover:bg-black/10 transition-colors">
          <X size={18} strokeWidth={2} className="text-black/60" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// ── Confirm dialog ─────────────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel, confirmLabel = 'Подтвердить', danger = false }: {
  message: string; onConfirm: () => void; onCancel: () => void; confirmLabel?: string; danger?: boolean;
}) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-white rounded-[20px] w-full max-w-xs shadow-2xl z-10 overflow-hidden">
      <div className="p-6 text-center">
        <p className="text-[16px] text-black leading-relaxed">{message}</p>
      </div>
      <div className="border-t border-black/8 flex">
        <button onClick={onCancel} className="flex-1 py-3.5 text-[17px] font-medium text-[#007AFF] hover:bg-[#F2F2F7] transition-colors border-r border-black/8">
          Отмена
        </button>
        <button onClick={onConfirm} className={`flex-1 py-3.5 text-[17px] font-semibold transition-colors hover:bg-[#F2F2F7] ${danger ? 'text-[#FF3B30]' : 'text-[#007AFF]'}`}>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ── Toggle ─────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
  <button
    onClick={() => onChange(!checked)}
    className={`w-[51px] h-[31px] rounded-full p-0.5 transition-colors duration-300 ease-in-out border border-black/5 flex items-center ${checked ? 'bg-[#34C759]' : 'bg-[#E9E9EB]'}`}
  >
    <div className={`w-[27px] h-[27px] bg-white rounded-full shadow-[0_3px_8px_rgba(0,0,0,0.15),0_1px_1px_rgba(0,0,0,0.16)] transition-transform duration-300 ease-in-out ${checked ? 'translate-x-[18px]' : 'translate-x-0'}`} />
  </button>
);

// ── SettingsScreen ─────────────────────────────────────────────────────
export const SettingsScreen = () => {
  const {
    operators,
    currentOperatorId,
    templates,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    setCurrentOperator,
    createOperatorApi,
    updateOperatorApi,
    setLoadError,
  } = useStore();
  const me = operators.find(o => o.id === currentOperatorId);
  const { logout, setOperator: setAuthOperator } = useAuthStore();
  const roleLabel = (role?: Operator['role']) =>
    role === 'OWNER' ? 'Owner' : role === 'ADMIN' ? 'Администратор' : role === 'VIEWER' ? 'Наблюдатель' : 'Оператор';

  // Notifications
  const [notifNewTickets, setNotifNewTickets] = useState(true);
  const [notifNewMessages, setNotifNewMessages] = useState(true);
  const [notifAssigned, setNotifAssigned] = useState(false);
  const [notifMentions, setNotifMentions] = useState(true);

  // Modals
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showAddOperator, setShowAddOperator] = useState(false);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [editingOperator, setEditingOperator] = useState<Operator | null>(null);

  // Toast
  const [toast, setToast] = useState('');
  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2200); };
  const [telegramStatus, setTelegramStatus] = useState<TelegramOperationalStatus | null>(null);
  const [telegramStatusLoading, setTelegramStatusLoading] = useState(false);
  const [telegramStatusError, setTelegramStatusError] = useState<string | null>(null);

  // Add Operator Form
  const [newOpName, setNewOpName] = useState('');
  const [newOpRole, setNewOpRole] = useState<Operator['role']>('OPERATOR');
  const [newOpTelegramId, setNewOpTelegramId] = useState('');
  const [newOpLogin, setNewOpLogin] = useState('');
  const [newOpPassword, setNewOpPassword] = useState('');

  // Add/Edit Template Form
  const [tplTitle, setTplTitle] = useState('');
  const [tplBody, setTplBody] = useState('');
  const [profileName, setProfileName] = useState(me?.name || '');
  const [profileLogin, setProfileLogin] = useState(me?.login || '');
  const [profileTelegramId, setProfileTelegramId] = useState(me?.telegramId || '');
  const [profileCurrentPassword, setProfileCurrentPassword] = useState('');
  const [profileNewPassword, setProfileNewPassword] = useState('');

  const handleAddOperator = async () => {
    if (!newOpName.trim()) return;
    await createOperatorApi({
      displayName: newOpName.trim(),
      role: newOpRole,
      telegramId: newOpTelegramId.trim() || undefined,
      login: newOpLogin.trim() || undefined,
      password: newOpPassword || undefined,
      isActive: true,
    });
    setNewOpName('');
    setNewOpRole('OPERATOR');
    setNewOpTelegramId('');
    setNewOpLogin('');
    setNewOpPassword('');
    setShowAddOperator(false);
    showToast('Оператор добавлен');
  };

  const handleSaveTemplate = async () => {
    if (!tplTitle.trim() || !tplBody.trim()) return;
    if (editingTemplate) {
      await updateTemplate(editingTemplate.id, { title: tplTitle.trim(), body: tplBody.trim() });
      showToast('Шаблон обновлён');
    } else {
      await addTemplate({ title: tplTitle.trim(), body: tplBody.trim() });
      showToast('Шаблон добавлен');
    }
    setTplTitle('');
    setTplBody('');
    setEditingTemplate(null);
    setShowAddTemplate(false);
  };

  const openEditTemplate = (tpl: Template) => {
    setEditingTemplate(tpl);
    setTplTitle(tpl.title);
    setTplBody(tpl.body);
    setShowAddTemplate(true);
  };

  const handleDeleteTemplate = async (id: string) => {
    await deleteTemplate(id);
    setDeletingTemplateId(null);
    showToast('Шаблон удалён');
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    void logout();
  };

  const handleSaveProfile = async () => {
    if (!me) return;
    const updated = await api.updateCurrentOperator({
      displayName: profileName.trim() || undefined,
      login: profileLogin.trim() || '',
      telegramId: profileTelegramId.trim() || '',
      currentPassword: profileCurrentPassword || undefined,
      newPassword: profileNewPassword || undefined,
    });
    await updateOperatorApi(updated.id, {
      displayName: updated.name,
      login: updated.login ?? null,
      telegramId: updated.telegramId ?? null,
    });
    setAuthOperator(updated);
    setProfileCurrentPassword('');
    setProfileNewPassword('');
    setShowEditProfile(false);
    showToast('Профиль обновлён');
  };

  const loadTelegramStatus = async () => {
    setTelegramStatusLoading(true);
    setTelegramStatusError(null);
    try {
      const status = await api.getTelegramOperationalStatus();
      setTelegramStatus(status);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось загрузить диагностику Telegram.';
      setTelegramStatusError(message);
      setLoadError(message);
    } finally {
      setTelegramStatusLoading(false);
    }
  };

  useEffect(() => {
    void loadTelegramStatus();
  }, []);

  return (
    <div className="flex-1 h-full bg-[#F5F5F7] flex flex-col mobile-content-safe overflow-y-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-black/80 text-white text-[14px] font-medium px-4 py-2 rounded-full backdrop-blur-xl shadow-lg pointer-events-none">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="px-6 py-5 bg-[#F5F5F7]/90 backdrop-blur-2xl sticky top-0 z-10 border-b border-black/5 shrink-0">
        <h2 className="text-[34px] font-bold text-black tracking-tight max-w-3xl mx-auto w-full">Настройки</h2>
      </div>

      <div className="flex-1 px-4 py-8 max-w-3xl w-full mx-auto space-y-8">
        {/* Account */}
        <section>
          <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest mb-3 ml-4">Аккаунт</h3>
          <div className="bg-white rounded-[20px] overflow-hidden border border-black/5 shadow-sm">
            <div className="p-5 flex items-center gap-4 border-b border-black/5">
              <Avatar name={me?.name || ''} size="68px" isOnline={me?.isOnline} />
              <div className="flex-1">
                <div className="text-[20px] font-semibold tracking-tight text-black">{me?.name}</div>
                <div className="text-[15px] font-medium text-[#8E8E93] mt-0.5">{roleLabel(me?.role)}</div>
              </div>
              <button
                onClick={() => {
                  setProfileName(me?.name || '');
                  setProfileLogin(me?.login || '');
                  setProfileTelegramId(me?.telegramId || '');
                  setShowEditProfile(true);
                }}
                className="p-2 rounded-full bg-[#F2F2F7] hover:bg-black/10 transition-colors"
              >
                <Edit2 size={16} className="text-[#8E8E93]" />
              </button>
            </div>

            {/* Switch operator (demo) */}
            <div className="border-b border-black/5">
              <div className="px-4 py-2 text-[11px] font-bold text-[#8E8E93] uppercase tracking-widest">Переключить аккаунт</div>
              {operators.map((op, i) => (
                <button
                  key={op.id}
                  onClick={() => { setCurrentOperator(op.id); showToast(`Теперь вы: ${op.name}`); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F9F9F9] transition-colors ${i !== operators.length - 1 ? 'border-b border-black/5' : ''}`}
                >
                  <Avatar name={op.name} size="32px" isOnline={op.isOnline} />
                  <span className="text-[15px] text-black flex-1 text-left">{op.name}</span>
                  <span className="text-[13px] text-[#8E8E93]">{roleLabel(op.role)}</span>
                  {op.id === currentOperatorId && <Check size={16} className="text-[#007AFF]" strokeWidth={2.5} />}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full p-4 flex items-center justify-between text-[#FF3B30] hover:bg-[#FFF5F5] transition-colors"
            >
              <span className="text-[17px] font-medium ml-1">Выйти</span>
              <LogOut size={20} strokeWidth={2} />
            </button>
          </div>
        </section>

        {/* Operators */}
        <section>
          <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest mb-3 ml-4">Операторы</h3>
          <div className="bg-white rounded-[20px] overflow-hidden border border-black/5 shadow-sm">
            {operators.map((op, i) => (
              <button
                key={op.id}
                onClick={() => { setEditingOperator(op); }}
                className={`w-full p-4 flex items-center justify-between hover:bg-[#F9F9F9] transition-colors ${i !== operators.length - 1 ? 'border-b border-black/5' : ''}`}
              >
                <div className="flex items-center gap-3.5">
                  <Avatar name={op.name} size="40px" isOnline={op.isOnline} />
                  <div className="text-left">
                    <div className="text-[16px] font-medium text-black">{op.name}</div>
                    <div className="text-[13px] text-[#8E8E93]">{roleLabel(op.role)} • {op.isOnline ? 'Онлайн' : 'Офлайн'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {op.id === currentOperatorId && (
                    <span className="text-[11px] font-bold text-[#007AFF] bg-[#007AFF]/10 px-2 py-0.5 rounded-full">Вы</span>
                  )}
                  <ChevronRight size={18} className="text-[#C7C7CC]" strokeWidth={2} />
                </div>
              </button>
            ))}
            <button
              onClick={() => setShowAddOperator(true)}
              className="w-full p-4 flex items-center gap-3 text-[#007AFF] hover:bg-[#F0F8FF] transition-colors border-t border-black/5"
            >
              <div className="w-8 h-8 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                <Plus size={20} strokeWidth={2.5} />
              </div>
              <span className="text-[17px] font-medium">Добавить оператора</span>
            </button>
          </div>
        </section>

        {/* Templates */}
        <section>
          <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest mb-3 ml-4">Шаблоны ответов</h3>
          <div className="bg-white rounded-[20px] overflow-hidden border border-black/5 shadow-sm">
            {templates.length === 0 && (
              <div className="p-6 text-center text-[15px] text-[#8E8E93]">Нет шаблонов</div>
            )}
            {templates.map((tpl, i) => (
              <div
                key={tpl.id}
                className={`p-4 flex items-center gap-3 ${i !== templates.length - 1 ? 'border-b border-black/5' : ''}`}
              >
                <div className="flex-1 min-w-0 pr-2 cursor-pointer" onClick={() => openEditTemplate(tpl)}>
                  <div className="text-[16px] font-medium text-black truncate mb-0.5">{tpl.title}</div>
                  <div className="text-[13px] text-[#8E8E93] truncate">{tpl.body}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEditTemplate(tpl)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#007AFF]/10 transition-colors text-[#007AFF]"
                  >
                    <Edit2 size={15} />
                  </button>
                  <button
                    onClick={() => setDeletingTemplateId(tpl.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#FF3B30]/10 transition-colors text-[#FF3B30]"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={() => { setEditingTemplate(null); setTplTitle(''); setTplBody(''); setShowAddTemplate(true); }}
              className="w-full p-4 flex items-center gap-3 text-[#007AFF] hover:bg-[#F0F8FF] transition-colors border-t border-black/5"
            >
              <div className="w-8 h-8 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                <Plus size={20} strokeWidth={2.5} />
              </div>
              <span className="text-[17px] font-medium">Новый шаблон</span>
            </button>
          </div>
        </section>

        {/* Notifications */}
        <section className="mb-8">
          <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest mb-3 ml-4">Уведомления</h3>
          <div className="bg-white rounded-[20px] overflow-hidden border border-black/5 shadow-sm">
            {[
              { label: 'Новые тикеты', val: notifNewTickets, set: setNotifNewTickets },
              { label: 'Новые сообщения', val: notifNewMessages, set: setNotifNewMessages },
              { label: 'Назначение тикета', val: notifAssigned, set: setNotifAssigned },
              { label: 'Упоминания', val: notifMentions, set: setNotifMentions },
            ].map((item, i, arr) => (
              <div key={item.label} className={`p-4 flex items-center justify-between ${i !== arr.length - 1 ? 'border-b border-black/5' : ''}`}>
                <div className="flex items-center gap-3 ml-1">
                  {item.val ? <Bell size={18} className="text-[#007AFF]" /> : <BellOff size={18} className="text-[#8E8E93]" />}
                  <span className="text-[17px] font-medium text-black">{item.label}</span>
                </div>
                <Toggle checked={item.val} onChange={(v) => { item.set(v); showToast(v ? 'Уведомления включены' : 'Уведомления отключены'); }} />
              </div>
            ))}
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-[12px] font-bold text-[#8E8E93] uppercase tracking-widest mb-3 ml-4">Telegram Диагностика</h3>
          <div className="bg-white rounded-[20px] overflow-hidden border border-black/5 shadow-sm p-4 space-y-3">
            {telegramStatusLoading && <div className="text-[14px] text-[#8E8E93]">Загрузка статуса Telegram...</div>}
            {!telegramStatusLoading && telegramStatusError && (
              <div className="text-[14px] text-[#A40000] bg-[#FFECEC] border border-[#FFD3D3] rounded-[12px] p-3">{telegramStatusError}</div>
            )}
            {!telegramStatusLoading && telegramStatus && (
              <>
                <div className="text-[14px] text-black">
                  <span className="font-semibold">Режим:</span> {telegramStatus.mode}
                </div>
                <div className="text-[14px] text-black">
                  <span className="font-semibold">Команд в боте:</span> {telegramStatus.commands.length}
                </div>
                {telegramStatus.webhook && (
                  <>
                    <div className="text-[14px] break-all"><span className="font-semibold">Webhook URL:</span> {telegramStatus.webhook.url || 'не установлен'}</div>
                    <div className="text-[14px]"><span className="font-semibold">Pending updates:</span> {telegramStatus.webhook.pendingUpdateCount}</div>
                    {telegramStatus.webhook.lastErrorMessage && (
                      <div className="text-[14px] text-[#A40000] bg-[#FFF4E5] border border-[#FFE2B8] rounded-[12px] p-3">
                        <div className="font-semibold mb-1">Последняя ошибка Telegram webhook</div>
                        <div>{telegramStatus.webhook.lastErrorMessage}</div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => void loadTelegramStatus()}
                className="px-3 py-2 rounded-[10px] bg-[#F2F2F7] text-[13px] font-medium"
              >
                Обновить статус
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await api.syncTelegramCommands();
                    showToast(`Команды синхронизированы (${response.commands.length})`);
                    await loadTelegramStatus();
                  } catch (error) {
                    const message = error instanceof Error ? error.message : 'Не удалось синхронизировать команды';
                    setTelegramStatusError(message);
                    setLoadError(message);
                  }
                }}
                className="px-3 py-2 rounded-[10px] bg-[#007AFF] text-white text-[13px] font-medium"
              >
                Синхронизировать команды
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Logout Confirm */}
      {showLogoutConfirm && (
        <ConfirmDialog
          message="Вы уверены, что хотите выйти?"
          confirmLabel="Выйти"
          danger
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}

      {/* Delete Template Confirm */}
      {deletingTemplateId && (
        <ConfirmDialog
          message="Удалить этот шаблон?"
          confirmLabel="Удалить"
          danger
          onConfirm={() => handleDeleteTemplate(deletingTemplateId)}
          onCancel={() => setDeletingTemplateId(null)}
        />
      )}

      {/* Add Operator Modal */}
      {showAddOperator && (
        <Modal title="Добавить оператора" onClose={() => setShowAddOperator(false)}>
          <div className="space-y-4">
            <div>
              <label className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider block mb-2">Имя</label>
              <input
                value={newOpName}
                onChange={e => setNewOpName(e.target.value)}
                placeholder="Имя оператора"
                className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider block mb-2">Роль</label>
              <div className="bg-black/[0.06] p-[3px] rounded-lg flex text-[14px] font-medium shadow-inner">
                {(['OPERATOR', 'ADMIN'] as const).map(r => (
                  <button
                    key={r}
                    onClick={() => setNewOpRole(r)}
                    className={`flex-1 py-2 px-3 rounded-md transition-all text-center ${
                      newOpRole === r
                        ? 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12)] text-black'
                        : 'text-black/60 hover:text-black/80'
                    }`}
                  >
                    {roleLabel(r)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider block mb-2">Telegram ID</label>
              <input
                value={newOpTelegramId}
                onChange={e => setNewOpTelegramId(e.target.value)}
                placeholder="Например: 546473747"
                className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all"
              />
            </div>
            <div>
              <label className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider block mb-2">Логин (опционально)</label>
              <input
                value={newOpLogin}
                onChange={e => setNewOpLogin(e.target.value)}
                placeholder="Логин для входа по паролю"
                className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all"
              />
            </div>
            <div>
              <label className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider block mb-2">Пароль (опционально)</label>
              <input
                type="password"
                value={newOpPassword}
                onChange={e => setNewOpPassword(e.target.value)}
                placeholder="Если нужен вход по логину/паролю"
                className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all"
              />
            </div>
            <button
              onClick={handleAddOperator}
              disabled={!newOpName.trim()}
              className="w-full py-3.5 rounded-[14px] bg-[#007AFF] text-white text-[17px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 shadow-sm"
            >
              Добавить
            </button>
          </div>
        </Modal>
      )}

      {/* Add/Edit Template Modal */}
      {showAddTemplate && (
        <Modal
          title={editingTemplate ? 'Редактировать шаблон' : 'Новый шаблон'}
          onClose={() => { setShowAddTemplate(false); setEditingTemplate(null); }}
        >
          <div className="space-y-4">
            <div>
              <label className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider block mb-2">Название</label>
              <input
                value={tplTitle}
                onChange={e => setTplTitle(e.target.value)}
                placeholder="Например: Приветствие"
                className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[13px] font-semibold text-[#8E8E93] uppercase tracking-wider block mb-2">
                Текст шаблона
                <span className="ml-2 text-[11px] font-normal text-[#007AFF] normal-case tracking-normal">{'{{operator.name}} — имя оператора'}</span>
              </label>
              <textarea
                value={tplBody}
                onChange={e => setTplBody(e.target.value)}
                placeholder="Здравствуйте! Меня зовут {operator.name}..."
                rows={4}
                className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all resize-none"
              />
            </div>
            <button
              onClick={handleSaveTemplate}
              disabled={!tplTitle.trim() || !tplBody.trim()}
              className="w-full py-3.5 rounded-[14px] bg-[#007AFF] text-white text-[17px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 shadow-sm"
            >
              {editingTemplate ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </Modal>
      )}

      {showEditProfile && (
        <Modal title="Мой профиль" onClose={() => setShowEditProfile(false)}>
          <div className="space-y-4">
            <input value={profileName} onChange={(e) => setProfileName(e.target.value)} placeholder="Имя" className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all" />
            <input value={profileLogin} onChange={(e) => setProfileLogin(e.target.value)} placeholder="Логин" className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all" />
            <input value={profileTelegramId} onChange={(e) => setProfileTelegramId(e.target.value)} placeholder="Telegram ID" className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all" />
            <input type="password" value={profileCurrentPassword} onChange={(e) => setProfileCurrentPassword(e.target.value)} placeholder="Текущий пароль" className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all" />
            <input type="password" value={profileNewPassword} onChange={(e) => setProfileNewPassword(e.target.value)} placeholder="Новый пароль" className="w-full bg-[#F2F2F7] rounded-[12px] px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-[#007AFF]/40 transition-all" />
            <button onClick={handleSaveProfile} className="w-full py-3.5 rounded-[14px] bg-[#007AFF] text-white text-[17px] font-semibold hover:opacity-90 transition-opacity shadow-sm">
              Сохранить
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Operator Modal */}
      {editingOperator && (
        <Modal title="Оператор" onClose={() => setEditingOperator(null)}>
          <div className="space-y-5">
            <div className="flex flex-col items-center pb-4">
              <Avatar name={editingOperator.name} size="72px" isOnline={editingOperator.isOnline} />
              <div className="text-[20px] font-semibold mt-3">{editingOperator.name}</div>
              <div className="text-[14px] text-[#8E8E93]">{roleLabel(editingOperator.role)}</div>
            </div>

            <div className="space-y-1">
              <button
                onClick={async () => {
                  const nextIsActive = !(editingOperator.isActive ?? true);
                  await updateOperatorApi(editingOperator.id, { isActive: nextIsActive });
                  setEditingOperator({ ...editingOperator, isActive: nextIsActive });
                  showToast(nextIsActive ? 'Оператор активирован' : 'Оператор деактивирован');
                }}
                className="w-full flex items-center justify-between p-4 bg-[#F2F2F7] rounded-[14px] hover:bg-black/10 transition-colors"
              >
                <span className="text-[16px] font-medium text-black">Активный аккаунт</span>
                <Toggle checked={editingOperator.isActive ?? true} onChange={() => {}} />
              </button>

              <button
                onClick={async () => {
                  const nextRole = editingOperator.role === 'ADMIN' ? 'OPERATOR' : 'ADMIN';
                  await updateOperatorApi(editingOperator.id, { role: nextRole });
                  setEditingOperator({ ...editingOperator, role: nextRole });
                  showToast('Роль изменена');
                }}
                className="w-full flex items-center justify-between p-4 bg-[#F2F2F7] rounded-[14px] hover:bg-black/10 transition-colors"
              >
                <span className="text-[16px] font-medium text-black">Администратор</span>
                <Toggle checked={editingOperator.role === 'ADMIN'} onChange={() => {}} />
              </button>
            </div>

            <button
              onClick={() => setEditingOperator(null)}
              className="w-full py-3.5 rounded-[14px] bg-[#007AFF] text-white text-[17px] font-semibold hover:opacity-90 transition-opacity"
            >
              Готово
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};
